import { useState } from "react";
import { useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import useDatasetTables from "datasets/pages/tables/hooks/useDatasetTables";
import IngestionStatus from "datasets/pages/tables/types/enums/ingest-status";
import { useFetcher } from "~/hooks/useFetcher";
import { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

interface IngestionInfo {
  id: string;
  status: IngestionStatus;
  updated_at: string | null;
  created_at: string | null;
  last_start_date: string | null;
  completed_at: string | null;
  table_fqn: string;
  metadata_parse_errors?: string[];
}

const useActiveIngests = (id?: string) => {
  const { datasetId = id } = useParams();
  const { mutate: mutateDataset } = useDataset(datasetId);
  const { mutate: mutateTableFQNs } = useDatasetTableFQNs(true, datasetId);
  const { mutate: mutateDatasetTables } = useDatasetTables();
  const [prevPending, setPrevPending] = useState<IngestionInfo[]>([]);
  const [refreshInterval, setRefreshInterval] = useState(60000);

  const resp = useFetcher<IngestionInfo[]>(
    `${VITE_CATALOG_API}/datasets/${datasetId}/active-table-ingestions?include_recently_completed=true`,
    {
      refreshInterval,
      onSuccess: (data: IngestionInfo[]) => {
        const pending = data.filter((ingest) =>
          [IngestionStatus.PENDING, IngestionStatus.IN_PROGRESS].includes(ingest.status)
        );

        // Update refresh interval based on pending ingestinos
        const newRefreshInterval = pending.length > 0 ? 5000 : 60000; // 5 seconds if active, 1 minute if none
        setRefreshInterval(newRefreshInterval);

        // Previously pending ingest(s) completed, refresh dataset & tables
        if (pending.length < prevPending.length) {
          mutateDataset();
          mutateTableFQNs();
          mutateDatasetTables();

          const pendingIds = new Set(pending.map((i) => i.id));
          const justCompleted = prevPending.filter((i) => !pendingIds.has(i.id));
          for (const prev of justCompleted) {
            const current = data.find((i) => i.id === prev.id);
            if (current) {
              const durationMs =
                current.created_at && current.completed_at
                  ? new Date(current.completed_at).getTime() -
                    new Date(current.created_at).getTime()
                  : undefined;
              trackEvent("dataset_table_ingest_completed", {
                dataset_id: datasetId,
                table_fqn: current.table_fqn,
                status: current.status,
                completed_at: current.completed_at,
                duration_ms: durationMs,
                had_metadata_parse_errors: !!current.metadata_parse_errors?.length,
              });
            }
          }
        }

        setPrevPending(pending);
      },
    }
  );

  return resp;
};

export default useActiveIngests;
